import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ReactComponent as BackIcon } from "../../Assets/Categories/BackIcon.svg";
import SearchBar from "../Main/searchBar";
import { useDispatch } from "react-redux";
import { assignBuilderToFranchise } from "../../Redux/Actions/franchiseAction";
import toast from "react-hot-toast";
import SelectInput from "../../Global Functions/selectInput";
import "./formAssignBuilder.css";

const ACL_OPTIONS = [
  { label: "Booking", value: "booking" },
  { label: "Project", value: "project" },
  { label: "Property", value: "property" },
  { label: "Meeting", value: "meeting" },
  { label: "Site Visit", value: "site_visit" },
];

const ACCESS_TYPES = ["list", "view", "edit"];

const FormAssignBuilders = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { builderId, franchiseId, brandName } = location.state || {};

  // ACL names selected (array of strings)
  const [aclsSelected, setAclsSelected] = useState([]);

  // Access rights map by ACL name -> array of access strings
  const [accessByAcl, setAccessByAcl] = useState({});

  // Handle ACL selection update from SelectInput
  const handleAclChange = (e) => {
    const selectedAcls = e.target.value || [];

    // Remove access entries for ACLs that are unselected
    const newAccessByAcl = {};
    selectedAcls.forEach((acl) => {
      newAccessByAcl[acl] = accessByAcl[acl] || [];
    });

    setAclsSelected(selectedAcls);
    setAccessByAcl(newAccessByAcl);
  };

  // Toggle individual access right for an ACL
  const handleAccessToggle = (aclName, accessType) => {
    setAccessByAcl((prev) => {
      const currentAccess = prev[aclName] || [];
      const hasAccess = currentAccess.includes(accessType);
      const updatedAccess = hasAccess
        ? currentAccess.filter((a) => a !== accessType)
        : [...currentAccess, accessType];
      return { ...prev, [aclName]: updatedAccess };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!builderId || !franchiseId) {
      toast.error("Missing builder ID or franchise ID");
      return;
    }
    if (aclsSelected.length === 0) {
      toast.error("Please select at least one ACL");
      return;
    }
    // Validate access rights for each ACL selected
    for (const acl of aclsSelected) {
      if (!accessByAcl[acl] || accessByAcl[acl].length === 0) {
        toast.error(`Please select at least one access right for "${acl}"`);
        return;
      }
    }

    // Prepare payload acls array
    const aclsPayload = aclsSelected.map((name) => ({
      name,
      access: accessByAcl[name],
    }));

    const payload = {
      franchise_id: franchiseId,
      builder_id: builderId,
      acls: aclsPayload,
    };

    const result = await dispatch(assignBuilderToFranchise(payload));
    if (result?.statusCode === 200) {
      toast.success("Builder assigned successfully");
      navigate("/franchise/assign-builder-list/" + franchiseId);
    }
  };

  return (
    <div className="grid-container">
      {/* Search Bar */}
      <div
        className="padding-20"
        style={{ gridColumn: "3/8", marginBottom: 24 }}
      >
        <SearchBar style={{ width: "80%" }} />
      </div>

      {/* Back Icon & Title */}
      <div
        className="back-icon margin-0 gap-12 align-center flex-container"
        style={{
          gridColumn: "span 7",
          marginBottom: 24,
          justifyContent: "space-between",
          borderBottom: "1px solid #ccc",
          paddingBottom: 12,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <Link
            to={`/franchise/assign-builder/${franchiseId}`}
            state={{ franchiseId }}
          >
            <BackIcon style={{ cursor: "pointer", width: 80, height: 80 }} />
          </Link>
          <h3 style={{ margin: 0, fontWeight: 700, color: "#181E2E" }}>
            Assign Builder
          </h3>
        </div>
      </div>

      {/* Form Container */}
      <div
        className="main-div"
        style={{ gridColumn: "1 / -1", leftGap: 20, marginTop: 20 }}
      >
        <div
          className="form-container"
          style={{ width: "80%", margin: "0 auto" }}
        >
          {/* IDs Display */}
          <div className="ids-display" style={{ marginBottom: 24 }}>
            <p>
              <strong style={{ fontSize: "1.2em", fontWeight: "bold" }}>
                Builder Name:
              </strong>{" "}
              <span style={{ fontSize: "1.15em", fontWeight: "bold" }}>
                {brandName ?? "No Builder Name provided"}
              </span>
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div className="form-section" style={{ marginBottom: 20 }}>
              <SelectInput
                label="Select ACL Names"
                name="aclsSelected"
                placeholder="Select ACL(s)..."
                options={ACL_OPTIONS}
                value={aclsSelected}
                onChange={handleAclChange}
                multiSelect={true}
                filled={true}
              />
            </div>

            {/* Access rights for each selected ACL */}
            {aclsSelected.map((aclName) => (
              <div
                key={aclName}
                className="access-rights-container"
                style={{ marginBottom: 20 }}
              >
                <p className="access-rights-header">
                  Access rights for&nbsp;
                  <strong>
                    {aclName.charAt(0).toUpperCase() + aclName.slice(1)}
                  </strong>
                  :
                </p>
                <div className="checkbox-container">
                  {ACCESS_TYPES.map((access) => (
                    <label
                      key={access}
                      className="custom-checkbox-label"
                      style={{
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={(accessByAcl[aclName] || []).includes(access)}
                        onChange={() => handleAccessToggle(aclName, access)}
                      />
                      <span style={{ textTransform: "capitalize" }}>
                        {access}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            ))}

            <button type="submit" className="submit-button">
              Assign Builder
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default FormAssignBuilders;

